// src/frontend/src/pages/SolutionPage.tsx
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import HomeLayout from '../components/Layout/HomeLayout';

interface SolutionContent {
  title: string;
  subtitle: string;
  heroDescription: string;
  targetAudience: string;
  painPoints: string[];
  solutions: {
    title: string;
    description: string;
    features: string[];
  }[];
  successMetrics: {
    metric: string;
    description: string;
    icon: string;
  }[];
  testimonial?: {
    quote: string;
    author: string;
    role: string;
    company: string;
  };
  recommendedTrack?: string;
  ctaTitle: string;
  ctaDescription: string;
}

const solutionData: Record<string, SolutionContent> = {
  'new-project-managers': {
    title: 'PM Coaching for New Project Managers',
    subtitle: 'Build confidence in stakeholder conversations from day one',
    heroDescription: 'Starting your PM career? Master the fundamental stakeholder conversations that separate successful PMs from those who struggle. Our New PM Essentials track provides the foundation you need to communicate with authority and professionalism.',
    targetAudience: 'Project managers with 0-3 years of experience, recent PMP certification holders, and professionals transitioning into PM roles',
    painPoints: [
      'Feeling unprepared for difficult stakeholder conversations',
      'Lacking confidence when communicating with executives',
      'Struggling to manage up effectively',
      'Difficulty handling pushback on timelines or scope',
      'Impostor syndrome in senior stakeholder meetings'
    ],
    solutions: [
      {
        title: 'Fundamental Communication Skills',
        description: 'Learn the core conversation patterns every PM needs',
        features: [
          'Status update best practices',
          'How to deliver bad news professionally',
          'Managing up to executives',
          'Team meeting facilitation'
        ]
      },
      {
        title: 'Confidence Building',
        description: 'Practice in a safe environment before real stakes',
        features: [
          'Low-pressure scenario practice',
          'Progressive difficulty increases',
          'Immediate feedback and improvement tips',
          'Mistake-safe learning environment'
        ]
      },
      {
        title: 'Real-World Application',
        description: 'Bridge the gap between theory and practice',
        features: [
          'Industry-realistic scenarios',
          'Common PM challenges and solutions',
          'Templates and scripts for difficult conversations',
          'Interview preparation for PM roles'
        ]
      }
    ],
    successMetrics: [
      {
        metric: '85%',
        description: 'of new PMs report feeling more confident after 2 weeks',
        icon: '💪'
      },
      {
        metric: '3x',
        description: 'faster onboarding to senior stakeholder meetings',
        icon: '⚡'
      },
      {
        metric: '47%',
        description: 'higher promotion rate within first 2 years',
        icon: '📈'
      }
    ],
    testimonial: {
      quote: 'PM Coach gave me the confidence I needed as a new PM. I went from dreading stakeholder meetings to actually looking forward to them. The practice scenarios felt so real.',
      author: 'Priya Patel',
      role: 'Junior PM',
      company: 'Healthcare Innovations'
    },
    recommendedTrack: 'New PM Essentials Learning Track',
    ctaTitle: 'Start Building PM Confidence Today',
    ctaDescription: 'Join thousands of new PMs who chose practice over anxiety. Get 20 coaching sessions free.'
  },
  'stakeholder-management': {
    title: 'Master Difficult Stakeholder Conversations',
    subtitle: 'Turn challenging relationships into collaborative partnerships',
    heroDescription: '75% of projects fail due to poor stakeholder communication. Master the conversations that make or break your projects with realistic practice and AI-powered coaching designed specifically for PM challenges.',
    targetAudience: 'Project managers dealing with difficult stakeholders, scope creep, budget constraints, or executive pressure',
    painPoints: [
      'Stakeholders who constantly change requirements',
      'Executives demanding impossible timelines',
      'Team members resistant to change',
      'Clients who don\'t understand technical limitations',
      'Cross-functional conflicts and competing priorities'
    ],
    solutions: [
      {
        title: 'Difficult Conversation Mastery',
        description: 'Learn to navigate the toughest stakeholder situations',
        features: [
          'Saying "no" while maintaining relationships',
          'Managing scope creep diplomatically',
          'Handling angry or frustrated stakeholders',
          'Negotiating timeline and budget constraints'
        ]
      },
      {
        title: 'Relationship Building',
        description: 'Transform adversarial relationships into partnerships',
        features: [
          'Understanding stakeholder motivations',
          'Building trust and credibility',
          'Creating win-win solutions',
          'Maintaining long-term relationships'
        ]
      },
      {
        title: 'Crisis Communication',
        description: 'Handle project crises with grace and professionalism',
        features: [
          'Emergency stakeholder communications',
          'Damage control and recovery',
          'Transparent progress reporting',
          'Escalation management'
        ]
      }
    ],
    successMetrics: [
      {
        metric: '28%',
        description: 'improvement in stakeholder satisfaction scores',
        icon: '😊'
      },
      {
        metric: '65%',
        description: 'reduction in scope creep incidents',
        icon: '🎯'
      },
      {
        metric: '40%',
        description: 'fewer project escalations',
        icon: '🔻'
      }
    ],
    testimonial: {
      quote: 'I used to avoid difficult stakeholder conversations. Now I handle them proactively. PM Coach taught me that these conversations are actually opportunities to strengthen relationships.',
      author: 'Michael Rodriguez',
      role: 'Senior PM',
      company: 'Global Finance Corp'
    },
    recommendedTrack: 'All Learning Tracks include stakeholder management scenarios',
    ctaTitle: 'Master Stakeholder Management',
    ctaDescription: 'Transform difficult relationships into project success stories.'
  },
  'scope-creep-management': {
    title: 'Stop Scope Creep Before It Starts',
    subtitle: 'Learn to manage changing requirements while maintaining relationships',
    heroDescription: 'Scope creep kills projects and damages stakeholder relationships. Learn the communication strategies that help you manage changing requirements professionally while keeping projects on track.',
    targetAudience: 'PMs struggling with changing requirements, feature creep, and stakeholder requests that threaten project success',
    painPoints: [
      '"Quick" feature requests that derail timelines',
      'Stakeholders who don\'t understand impact of changes',
      'Pressure to say "yes" to everything',
      'Difficulty quantifying the cost of scope changes',
      'Team frustration with constant requirement changes'
    ],
    solutions: [
      {
        title: 'Proactive Communication',
        description: 'Set clear expectations from the beginning',
        features: [
          'Scope definition and documentation',
          'Change request processes',
          'Impact assessment communication',
          'Stakeholder education on project constraints'
        ]
      },
      {
        title: 'Diplomatic Resistance',
        description: 'Say no without damaging relationships',
        features: [
          'Alternative solution proposals',
          'Future phase planning',
          'Cost-benefit analysis presentation',
          'Win-win negotiation techniques'
        ]
      },
      {
        title: 'Change Management',
        description: 'Handle unavoidable scope changes professionally',
        features: [
          'Formal change request handling',
          'Timeline and budget renegotiation',
          'Team communication about changes',
          'Stakeholder approval processes'
        ]
      }
    ],
    successMetrics: [
      {
        metric: '60%',
        description: 'reduction in scope creep incidents',
        icon: '🛡️'
      },
      {
        metric: '35%',
        description: 'improvement in project timeline adherence',
        icon: '⏰'
      },
      {
        metric: '50%',
        description: 'fewer budget overruns',
        icon: '💰'
      }
    ],
    ctaTitle: 'Take Control of Project Scope',
    ctaDescription: 'Learn the conversations that keep projects on track and stakeholders happy.'
  },
'senior-project-managers': {
  title: 'Advanced PM Coaching for Senior Project Managers',
  subtitle: 'Master complex stakeholder dynamics and strategic communication',
  heroDescription: 'Ready to lead at the next level? Senior PMs face unique challenges - from managing executive expectations to navigating complex organizational politics. Practice advanced stakeholder scenarios designed for experienced PMs seeking leadership roles.',
  targetAudience: 'Project managers with 3-8 years of experience, Senior PMs seeking promotion to Lead/Principal PM roles, and experienced PMs managing complex, multi-stakeholder projects',
  painPoints: [
    'Managing competing priorities across multiple executives',
    'Navigating organizational politics and stakeholder conflicts',
    'Communicating complex technical decisions to business leaders',
    'Leading cross-functional teams without direct authority',
    'Building consensus among diverse, senior stakeholder groups',
    'Managing up to C-level executives effectively'
  ],
  solutions: [
    {
      title: 'Executive Communication Mastery',
      description: 'Learn to communicate with C-level executives and board members',
      features: [
        'Strategic project communication frameworks',
        'Executive briefing and presentation skills',
        'Handling board-level project reviews',
        'Managing executive sponsor relationships'
      ]
    },
    {
      title: 'Complex Stakeholder Orchestration',
      description: 'Navigate multi-layered stakeholder environments',
      features: [
        'Managing stakeholder conflicts and competing interests',
        'Building consensus across departments and regions',
        'Facilitating high-stakes stakeholder negotiations',
        'Cross-functional leadership without authority'
      ]
    },
    {
      title: 'Strategic Problem Solving',
      description: 'Handle ambiguous, high-impact project challenges',
      features: [
        'Managing projects with unclear requirements',
        'Strategic decision-making under uncertainty',
        'Risk communication to senior leadership',
        'Change management for large-scale initiatives'
      ]
    }
  ],
  successMetrics: [
    {
      metric: '63%',
      description: 'of Senior PMs promoted to Lead/Principal roles within 18 months',
      icon: '📈'
    },
    {
      metric: '45%',
      description: 'improvement in executive stakeholder satisfaction scores',
      icon: '⭐'
    },
    {
      metric: '78%',
      description: 'better at managing complex, multi-stakeholder projects',
      icon: '🎯'
    }
  ],
  testimonial: {
    quote: 'The senior PM scenarios prepared me for the complexity I face daily. I now confidently manage 15+ stakeholders across different time zones and business units. Got promoted to Principal PM last quarter.',
    author: 'Alex Chen',
    role: 'Principal PM',
    company: 'Fortune 500 Technology Company'
  },
  recommendedTrack: 'Senior PM Challenges Learning Track',
  ctaTitle: 'Master Senior-Level PM Skills',
  ctaDescription: 'Practice advanced scenarios designed for experienced PMs ready to lead at the next level.'
},
'pmo-leaders': {
  title: 'PMO Leadership Coaching for Directors and VPs',
  subtitle: 'Master portfolio management and organizational transformation',
  heroDescription: 'Leading a PMO requires a unique blend of strategic vision and operational excellence. Practice high-stakes conversations with executives, manage organizational change, and build PMO credibility through realistic scenarios designed for PMO directors and VPs.',
  targetAudience: 'PMO Directors, VP of Project Management, Portfolio Managers, and Senior PMs transitioning to PMO leadership roles',
  painPoints: [
    'Establishing PMO credibility and value proposition',
    'Managing portfolio prioritization and resource allocation',
    'Driving organizational change and PMO adoption',
    'Communicating portfolio health to board and executives',
    'Building and scaling PMO capabilities across the organization',
    'Handling resistance to PMO processes and governance'
  ],
  solutions: [
    {
      title: 'Portfolio Strategy & Governance',
      description: 'Master portfolio-level decision making and governance',
      features: [
        'Portfolio prioritization and resource allocation decisions',
        'Board-level portfolio reporting and communication',
        'Strategic project selection and portfolio optimization',
        'Executive stakeholder management at portfolio level'
      ]
    },
    {
      title: 'Organizational Change Leadership',
      description: 'Drive PMO adoption and organizational transformation',
      features: [
        'Managing resistance to PMO processes',
        'Building PMO credibility and demonstrating value',
        'Scaling project management maturity organization-wide',
        'Leading cultural transformation initiatives'
      ]
    },
    {
      title: 'Executive Partnership',
      description: 'Build strategic partnerships with C-level executives',
      features: [
        'Board presentation and portfolio communication',
        'Strategic business case development for PMO initiatives',
        'Executive coaching and PM capability building',
        'Cross-departmental collaboration and influence'
      ]
    }
  ],
  successMetrics: [
    {
      metric: '85%',
      description: 'of PMO leaders report improved executive relationships',
      icon: '🤝'
    },
    {
      metric: '92%',
      description: 'successfully implement new PMO processes after training',
      icon: '⚙️'
    },
    {
      metric: '67%',
      description: 'increase in perceived PMO value by executive stakeholders',
      icon: '📊'
    }
  ],
  testimonial: {
    quote: 'PM Coach prepared me for the political complexity of PMO leadership. I learned how to position the PMO as a strategic enabler, not just a process enforcer. Our executive satisfaction with the PMO improved dramatically.',
    author: 'Sarah Johnson',
    role: 'VP of Project Management Office',
    company: 'Global Manufacturing Corporation'
  },
  recommendedTrack: 'PMO Leadership Track',
  ctaTitle: 'Elevate Your PMO Leadership',
  ctaDescription: 'Master the strategic conversations that define successful PMO leadership.'
},
'pm-interview-prep': {
  title: 'PM Interview Preparation & Behavioral Coaching',
  subtitle: 'Ace your PM interviews with realistic practice scenarios',
  heroDescription: 'Land your dream PM role with targeted interview preparation. Practice common PM behavioral questions, stakeholder scenario discussions, and case study presentations with AI interviewers that simulate real hiring processes at top tech companies.',
  targetAudience: 'PM candidates preparing for interviews at top companies, professionals transitioning into PM roles, and current PMs seeking new opportunities or promotions',
  painPoints: [
    'Struggling with behavioral interview questions about stakeholder management',
    'Difficulty articulating past PM experiences in compelling ways',
    'Nervousness when discussing conflict resolution and difficult situations',
    'Lack of practice with case study presentations and problem-solving',
    'Uncertainty about how to demonstrate PM leadership and influence skills'
  ],
  solutions: [
    {
      title: 'Behavioral Interview Mastery',
      description: 'Perfect your responses to common PM behavioral questions',
      features: [
        'Practice with 50+ common PM behavioral interview questions',
        'STAR method coaching for structuring compelling responses',
        'Stakeholder management scenario discussions',
        'Conflict resolution and problem-solving examples'
      ]
    },
    {
      title: 'Case Study & Presentation Skills',
      description: 'Excel at PM case studies and technical discussions',
      features: [
        'Product prioritization case study practice',
        'Technical trade-off discussion scenarios',
        'Stakeholder alignment case presentations',
        'Data-driven decision making examples'
      ]
    },
    {
      title: 'Company-Specific Preparation',
      description: 'Tailor your preparation for specific company cultures',
      features: [
        'FAANG-style behavioral interview practice',
        'Startup vs. enterprise PM role preparation',
        'Industry-specific scenario practice',
        'Company culture and value alignment coaching'
      ]
    }
  ],
  successMetrics: [
    {
      metric: '89%',
      description: 'of candidates report feeling more confident after practice',
      icon: '💪'
    },
    {
      metric: '73%',
      description: 'interview success rate for users who complete 10+ sessions',
      icon: '🎯'
    },
    {
      metric: '156%',
      description: 'average salary increase for successful candidates',
      icon: '💰'
    }
  ],
  testimonial: {
    quote: 'PM Coach interview prep was a game-changer. I practiced handling difficult stakeholder scenarios and landed a Senior PM role at Google. The behavioral interview coaching was spot-on with what they actually asked.',
    author: 'Jennifer Wu',
    role: 'Senior PM',
    company: 'Google'
  },
  recommendedTrack: 'All tracks provide excellent interview preparation content',
  ctaTitle: 'Ace Your Next PM Interview',
  ctaDescription: 'Practice realistic interview scenarios and land the PM role you want.'
},
// Add these to your solutionData object in src/frontend/src/pages/SolutionPage.tsx

'executive-communication': {
  title: 'Master Executive Communication',
  subtitle: 'Communicate effectively with C-level executives and senior leadership',
  heroDescription: 'Transform how you communicate with executives. Learn to present project status, negotiate resources, and influence strategic decisions with confidence. Master the executive mindset and communication style that gets results.',
  targetAudience: 'Senior PMs, PMO leaders, and project managers who regularly interact with C-level executives, VPs, and senior leadership',
  painPoints: [
    'Executives seem impatient during project updates',
    'Difficulty getting executive buy-in for project decisions',
    'Struggling to communicate technical concepts to business leaders',
    'Feeling intimidated in board rooms and executive meetings',
    'Projects get deprioritized due to poor executive communication',
    'Unable to secure necessary resources from leadership'
  ],
  solutions: [
    {
      title: 'Executive Presentation Mastery',
      description: 'Learn to structure and deliver compelling executive briefings',
      features: [
        'Bottom-line-up-front (BLUF) communication structure',
        'Data visualization for executive dashboards',
        'Handling executive questions and pushback',
        'Board-level project presentation skills'
      ]
    },
    {
      title: 'Strategic Influence & Negotiation',
      description: 'Master the art of influencing without authority',
      features: [
        'Building business cases that resonate with executives',
        'Resource negotiation and priority discussions',
        'Managing competing executive demands',
        'Strategic project positioning and messaging'
      ]
    },
    {
      title: 'Crisis & Escalation Management',
      description: 'Handle project crises with executive stakeholders',
      features: [
        'Emergency executive communications',
        'Managing project escalations professionally',
        'Transparent problem reporting and solutions',
        'Maintaining credibility during project challenges'
      ]
    }
  ],
  successMetrics: [
    {
      metric: '78%',
      description: 'improvement in executive stakeholder satisfaction',
      icon: '⭐'
    },
    {
      metric: '65%',
      description: 'more likely to get project approval and resources',
      icon: '✅'
    },
    {
      metric: '42%',
      description: 'faster decision-making from executive sponsors',
      icon: '⚡'
    }
  ],
  testimonial: {
    quote: 'PM Coach taught me to think like an executive. Now my project updates get their attention, and I consistently secure the resources my team needs. I even got invited to strategic planning sessions.',
    author: 'David Kim',
    role: 'Senior PM',
    company: 'Fortune 100 Technology Company'
  },
  recommendedTrack: 'Senior PM Challenges Learning Track',
  ctaTitle: 'Master Executive Communication',
  ctaDescription: 'Learn the communication skills that get projects approved and careers advanced.'
},

'team-leadership': {
  title: 'Project Team Leadership & Motivation',
  subtitle: 'Lead high-performing teams without direct authority',
  heroDescription: 'Master the art of leading project teams as a PM. Learn to motivate cross-functional teams, resolve conflicts, build trust, and drive results without being anyone\'s direct manager. Transform team dynamics and project outcomes.',
  targetAudience: 'Project managers leading cross-functional teams, PMs struggling with team motivation, and senior PMs transitioning to leadership roles',
  painPoints: [
    'Team members don\'t prioritize project work',
    'Difficulty motivating team members from different departments',
    'Conflicts between team members disrupting project progress',
    'Lack of engagement in team meetings and project activities',
    'Team members going around you to their direct managers',
    'Struggling to build trust and credibility with senior team members'
  ],
  solutions: [
    {
      title: 'Influence Without Authority',
      description: 'Master the core skills of PM leadership',
      features: [
        'Building trust and credibility with team members',
        'Motivational techniques for cross-functional teams',
        'Managing team members who outrank you',
        'Creating shared vision and team buy-in'
      ]
    },
    {
      title: 'Conflict Resolution & Team Dynamics',
      description: 'Handle team conflicts and improve collaboration',
      features: [
        'Mediating conflicts between team members',
        'Managing personality clashes and work style differences',
        'Facilitating productive team discussions',
        'Building psychological safety for honest communication'
      ]
    },
    {
      title: 'Performance Management & Accountability',
      description: 'Drive results through effective team management',
      features: [
        'Setting clear expectations and accountability',
        'Conducting effective one-on-ones with team members',
        'Recognizing and rewarding team contributions',
        'Managing underperformance without direct authority'
      ]
    }
  ],
  successMetrics: [
    {
      metric: '83%',
      description: 'improvement in team engagement scores',
      icon: '🎯'
    },
    {
      metric: '56%',
      description: 'reduction in team conflicts and escalations',
      icon: '🤝'
    },
    {
      metric: '71%',
      description: 'improvement in project delivery timelines',
      icon: '📈'
    }
  ],
  testimonial: {
    quote: 'I went from having team members avoid my calls to leading one of the most engaged project teams in the company. The leadership techniques are practical and work immediately.',
    author: 'Maria Santos',
    role: 'Lead PM',
    company: 'Global Manufacturing Corp'
  },
  recommendedTrack: 'Senior PM Challenges Learning Track',
  ctaTitle: 'Become a Confident Team Leader',
  ctaDescription: 'Master the leadership skills that turn project teams into high-performing units.'
},

'conflict-resolution': {
  title: 'Project Conflict Resolution Mastery',
  subtitle: 'Turn project conflicts into collaboration opportunities',
  heroDescription: 'Every project has conflicts - stakeholder disagreements, resource disputes, scope battles. Learn to navigate these conflicts professionally, find win-win solutions, and actually strengthen relationships through effective conflict resolution.',
  targetAudience: 'Project managers dealing with stakeholder conflicts, team disputes, resource competition, and organizational politics',
  painPoints: [
    'Stakeholders with competing priorities and conflicting requirements',
    'Team members who refuse to work together effectively',
    'Resource conflicts between departments and project teams',
    'Scope disputes and requirement disagreements',
    'Personality clashes affecting project progress',
    'Escalations that damage relationships and project credibility'
  ],
  solutions: [
    {
      title: 'Stakeholder Conflict Mediation',
      description: 'Navigate complex stakeholder disagreements',
      features: [
        'Identifying root causes behind stated positions',
        'Facilitating productive conflict resolution meetings',
        'Finding win-win solutions that satisfy multiple parties',
        'De-escalating heated stakeholder discussions'
      ]
    },
    {
      title: 'Team Conflict Management',
      description: 'Resolve team disputes and improve collaboration',
      features: [
        'Mediating between team members with different priorities',
        'Managing conflicts between functional departments',
        'Addressing work style and communication differences',
        'Rebuilding trust after conflicts occur'
      ]
    },
    {
      title: 'Preventive Conflict Management',
      description: 'Stop conflicts before they derail projects',
      features: [
        'Early conflict detection and intervention',
        'Setting clear expectations and boundaries',
        'Building processes that prevent common conflicts',
        'Creating psychological safety for honest discussions'
      ]
    }
  ],
  successMetrics: [
    {
      metric: '68%',
      description: 'reduction in project escalations and conflicts',
      icon: '🕊️'
    },
    {
      metric: '79%',
      description: 'of conflicts resolved without executive escalation',
      icon: '✅'
    },
    {
      metric: '84%',
      description: 'report stronger stakeholder relationships after conflict resolution',
      icon: '🤝'
    }
  ],
  testimonial: {
    quote: 'I used to dread stakeholder conflicts. Now I see them as opportunities to understand underlying needs and build stronger partnerships. My conflict resolution skills have become my biggest strength as a PM.',
    author: 'James Wilson',
    role: 'Senior PM',
    company: 'Healthcare Technology Solutions'
  },
  recommendedTrack: 'All Learning Tracks include conflict resolution scenarios',
  ctaTitle: 'Master Conflict Resolution',
  ctaDescription: 'Transform project conflicts from roadblocks into relationship-building opportunities.'
},

'budget-negotiations': {
  title: 'Project Budget Negotiations & Financial Management',
  subtitle: 'Secure funding and manage budget constraints like a pro',
  heroDescription: 'Budget discussions can make or break your project. Learn to build compelling financial cases, negotiate budget allocations, handle cost overruns professionally, and communicate financial impacts to stakeholders with confidence.',
  targetAudience: 'Project managers responsible for budget management, PMs facing funding constraints, and senior PMs involved in project financial planning',
  painPoints: [
    'Struggling to justify project budgets to finance and executives',
    'Dealing with mid-project budget cuts and cost constraints',
    'Difficulty communicating financial impacts to non-financial stakeholders',
    'Budget overruns and how to handle them professionally',
    'Competing with other projects for limited financial resources',
    'Complex budget approval processes and financial bureaucracy'
  ],
  solutions: [
    {
      title: 'Financial Case Development',
      description: 'Build compelling business cases and budget justifications',
      features: [
        'ROI calculation and financial modeling for projects',
        'Cost-benefit analysis and payback period presentations',
        'Risk-adjusted budget planning and contingency management',
        'Value-based budgeting and priority-based resource allocation'
      ]
    },
    {
      title: 'Budget Negotiation Strategies',
      description: 'Master the art of financial negotiations',
      features: [
        'Negotiating with finance teams and budget committees',
        'Handling budget cuts while maintaining project scope',
        'Creative funding solutions and resource optimization',
        'Multi-year budget planning and phased funding approaches'
      ]
    },
    {
      title: 'Financial Crisis Management',
      description: 'Handle budget overruns and financial constraints',
      features: [
        'Transparent communication about budget challenges',
        'Options analysis for budget constraint scenarios',
        'Stakeholder management during financial crises',
        'Recovery planning and financial course correction'
      ]
    }
  ],
  successMetrics: [
    {
      metric: '91%',
      description: 'of budget requests approved after training',
      icon: '💰'
    },
    {
      metric: '45%',
      description: 'better at staying within original budget constraints',
      icon: '🎯'
    },
    {
      metric: '73%',
      description: 'improvement in financial stakeholder satisfaction',
      icon: '📊'
    }
  ],
  testimonial: {
    quote: 'Budget conversations used to terrify me. Now I confidently present financial cases to the CFO and consistently secure funding for my projects. The negotiation frameworks really work.',
    author: 'Lisa Chang',
    role: 'Portfolio PM',
    company: 'Financial Services Firm'
  },
  recommendedTrack: 'Senior PM Challenges Learning Track',
  ctaTitle: 'Master Project Financial Management',
  ctaDescription: 'Learn the budget and negotiation skills that keep projects funded and on track.'
},

'timeline-pressure': {
  title: 'Managing Timeline Pressure & Impossible Deadlines',
  subtitle: 'Navigate deadline pressure while maintaining quality and team morale',
  heroDescription: 'Facing impossible deadlines and timeline pressure? Learn to negotiate realistic timelines, manage stakeholder expectations, accelerate delivery without burning out your team, and communicate timeline challenges professionally.',
  targetAudience: 'Project managers facing aggressive deadlines, PMs dealing with timeline pressure from executives, and teams struggling with unrealistic delivery expectations',
  painPoints: [
    'Executives demanding impossible timelines and delivery dates',
    'Pressure to commit to deadlines before understanding full scope',
    'Team burnout from constant timeline pressure and overtime',
    'Quality suffering due to rushed delivery schedules',
    'Stakeholder frustration when projects inevitably run late',
    'Difficulty saying "no" to unrealistic timeline requests'
  ],
  solutions: [
    {
      title: 'Timeline Negotiation & Reality Setting',
      description: 'Negotiate realistic timelines with stakeholders',
      features: [
        'Data-driven timeline estimation and validation',
        'Presenting timeline trade-offs and constraint discussions',
        'Stakeholder education about project complexity',
        'Alternative delivery approaches and phased rollouts'
      ]
    },
    {
      title: 'Accelerated Delivery Strategies',
      description: 'Speed up delivery without compromising quality',
      features: [
        'Parallel workstream planning and execution',
        'Scope prioritization and MVP definition',
        'Resource optimization and team augmentation',
        'Risk management for accelerated timelines'
      ]
    },
    {
      title: 'Pressure Management & Communication',
      description: 'Handle timeline stress and maintain team morale',
      features: [
        'Transparent timeline communication with all stakeholders',
        'Managing team stress and preventing burnout',
        'Escalation strategies when timelines become impossible',
        'Recovery planning when deadlines are missed'
      ]
    }
  ],
  successMetrics: [
    {
      metric: '67%',
      description: 'improvement in timeline estimation accuracy',
      icon: '⏰'
    },
    {
      metric: '54%',
      description: 'reduction in team overtime and burnout incidents',
      icon: '😌'
    },
    {
      metric: '82%',
      description: 'of negotiated timelines accepted by stakeholders',
      icon: '🤝'
    }
  ],
  testimonial: {
    quote: 'I learned to push back on impossible deadlines professionally and present realistic alternatives. My team respects me more, and we actually deliver faster because we plan properly from the start.',
    author: 'Marcus Thompson',
    role: 'Senior PM',
    company: 'E-commerce Platform Company'
  },
  recommendedTrack: 'All Learning Tracks include timeline management scenarios',
  ctaTitle: 'Master Timeline Management',
  ctaDescription: 'Learn to manage deadline pressure professionally while delivering great results.'
},

'remote-project-management': {
  title: 'Remote & Distributed Team Project Management',
  subtitle: 'Master project leadership across time zones and virtual environments',
  heroDescription: 'Leading distributed teams requires different skills than co-located projects. Master virtual stakeholder management, remote team leadership, asynchronous communication, and the unique challenges of managing projects across time zones.',
  targetAudience: 'Project managers leading remote or distributed teams, PMs managing global projects across time zones, and teams transitioning to hybrid work environments',
  painPoints: [
    'Difficulty building relationships and trust in virtual environments',
    'Communication challenges across multiple time zones',
    'Team members feeling disconnected and disengaged',
    'Reduced visibility into project progress and team performance',
    'Cultural differences and communication styles in global teams',
    'Technology challenges and virtual meeting fatigue'
  ],
  solutions: [
    {
      title: 'Virtual Stakeholder Management',
      description: 'Build strong relationships in digital environments',
      features: [
        'Effective virtual meeting facilitation and engagement',
        'Building trust and rapport through digital channels',
        'Cross-cultural communication and sensitivity',
        'Asynchronous stakeholder communication strategies'
      ]
    },
    {
      title: 'Distributed Team Leadership',
      description: 'Lead high-performing remote and hybrid teams',
      features: [
        'Remote team motivation and engagement techniques',
        'Virtual one-on-ones and performance management',
        'Time zone management and inclusive meeting scheduling',
        'Digital collaboration and productivity tools mastery'
      ]
    },
    {
      title: 'Remote Project Execution',
      description: 'Deliver successful projects in virtual environments',
      features: [
        'Digital project tracking and transparency methods',
        'Virtual project ceremonies and team rituals',
        'Remote risk management and issue resolution',
        'Hybrid work model optimization for project teams'
      ]
    }
  ],
  successMetrics: [
    {
      metric: '76%',
      description: 'improvement in remote team engagement scores',
      icon: '💻'
    },
    {
      metric: '69%',
      description: 'better project visibility across distributed teams',
      icon: '👀'
    },
    {
      metric: '84%',
      description: 'of remote stakeholders report strong working relationships',
      icon: '🌍'
    }
  ],
  testimonial: {
    quote: 'Managing a team across 4 time zones seemed impossible until I learned these remote PM techniques. Now our distributed team is more collaborative than many co-located teams I\'ve seen.',
    author: 'Anna Kowalski',
    role: 'Global PM',
    company: 'International Software Company'
  },
  recommendedTrack: 'All Learning Tracks include remote work scenarios',
  ctaTitle: 'Master Remote Project Management',
  ctaDescription: 'Lead successful projects and engaged teams regardless of location or time zone.'
}
  // Add more solution data as needed
};

const SolutionPage: React.FC = () => {
  const { useCase } = useParams<{ useCase: string }>();
  
  if (!useCase || !solutionData[useCase]) {
    return (
      <HomeLayout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Solution Not Found</h1>
          <p className="text-gray-600 mb-8">The solution you're looking for doesn't exist.</p>
          <Link to="/" className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">
            Back to Home
          </Link>
        </div>
      </HomeLayout>
    );
  }

  const solution = solutionData[useCase];

  return (
    <HomeLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="py-16 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6">
            {solution.title}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {solution.subtitle}
          </p>
          <p className="text-lg text-gray-700 max-w-4xl mx-auto mb-8">
            {solution.heroDescription}
          </p>
          <Link
            to="/register"
            className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors text-lg"
          >
            Start Free Trial
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </Link>
        </div>

        {/* Target Audience */}
        <div className="py-8 bg-blue-50 rounded-2xl mb-16">
          <div className="max-w-4xl mx-auto px-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Perfect For</h2>
            <p className="text-lg text-gray-700">{solution.targetAudience}</p>
          </div>
        </div>

        {/* Pain Points */}
        <div className="py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Sound Familiar?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {solution.painPoints.map((point, index) => (
                <div key={index} className="flex items-start bg-red-50 p-4 rounded-lg">
                  <svg className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Solutions */}
        <div className="py-16 bg-gray-50 rounded-2xl">
          <div className="max-w-6xl mx-auto px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              How PM Coach Helps
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {solution.solutions.map((item, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-600 mb-4">{item.description}</p>
                  <ul className="space-y-2">
                    {item.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start text-sm text-gray-700">
                        <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Success Metrics */}
        <div className="py-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Proven Results
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solution.successMetrics.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl mb-4">{metric.icon}</div>
                <div className="text-4xl font-bold text-blue-600 mb-2">{metric.metric}</div>
                <p className="text-gray-600">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonial */}
        {solution.testimonial && (
          <div className="py-16 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl">
            <div className="max-w-4xl mx-auto text-center px-8">
              <blockquote className="text-2xl font-medium text-gray-900 mb-6">
                "{solution.testimonial.quote}"
              </blockquote>
              <div className="text-gray-600">
                <div className="font-semibold">{solution.testimonial.author}</div>
                <div>{solution.testimonial.role} at {solution.testimonial.company}</div>
              </div>
            </div>
          </div>
        )}

        {/* Recommended Track */}
        {solution.recommendedTrack && (
          <div className="py-16">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Recommended Learning Path</h3>
              <p className="text-lg text-gray-600 mb-6">{solution.recommendedTrack}</p>
              <Link
                to="/learning-tracks"
                className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700"
              >
                View Learning Tracks
                <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl text-white text-center">
          <h2 className="text-3xl font-bold mb-4">{solution.ctaTitle}</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {solution.ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-medium rounded-lg hover:bg-gray-100"
            >
              Start Free Trial
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-3 border-2 border-white text-white rounded-lg hover:bg-white hover:text-blue-600"
            >
              Talk to Expert
            </Link>
          </div>
          <p className="text-blue-200 text-sm mt-4">
            14-day free trial • No credit card required • 20 coaching sessions included
          </p>
        </div>
      </div>
    </HomeLayout>
  );
};

export default SolutionPage;